import axios from 'axios';

// Resolve API base:
// 1) REACT_APP_API_URL if provided (preferred)
// 2) If running CRA dev on :3000, assume backend on :5000
// 3) Otherwise use current origin
let API_URL = process.env.REACT_APP_API_URL;

// If a baked env points to localhost but the app is running on a different host,
// treat it as invalid and auto-detect instead (fixes prod builds that used a dev .env).
const isBrowser = typeof window !== 'undefined';
const isEnvLocalhost = API_URL && /localhost|127\.0\.0\.1/i.test(API_URL);
const isPageLocalhost = isBrowser && /localhost|127\.0\.0\.1/i.test(window.location.hostname);

if (!API_URL || (isEnvLocalhost && !isPageLocalhost)) {
  if (isBrowser) {
    const { protocol, hostname, port } = window.location;
    if (port === '3000' || port === '5173' || port === '4173') {
      // Common dev ports: point to backend dev port 5000
      API_URL = `${protocol}//${hostname}:5000`;
    } else {
      API_URL = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    }
  } else {
    API_URL = 'http://localhost:5000';
  }
}

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 8000, // keep snappy; prevent long spinner stalls
});

let refreshPromise = null;

function clearStoredAuth() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_access_token');
  localStorage.removeItem('admin_access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_refresh_token');
  localStorage.removeItem('user_type');
  localStorage.removeItem('device_fingerprint');
  localStorage.removeItem('remember_me');
  localStorage.removeItem('token_expires');
}

function redirectRegularUserToLogin() {
  const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.dispatchEvent(
    new CustomEvent('AUTH_REQUIRED', {
      detail: { redirect },
    })
  );
}

api.interceptors.request.use((config) => {
  const isAdminRoute = (config.url || '').startsWith('/admin');
  const adminToken = localStorage.getItem('admin_access_token') || localStorage.getItem('access_token');
  const userToken = localStorage.getItem('user_access_token') || localStorage.getItem('access_token');
  const token = isAdminRoute ? adminToken : userToken;
  const deviceFingerprint = localStorage.getItem('device_fingerprint');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (deviceFingerprint) {
    config.headers['X-Device-Fingerprint'] = deviceFingerprint;
  }
  return config;
});

// Add response interceptor to handle device mismatch
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const message = error.response?.data?.message || '';
      const config = error.config || {};
      const userType = localStorage.getItem('user_type');
      const isExpiredUserToken = message.includes('Invalid or expired token');
      const refreshToken = localStorage.getItem('user_refresh_token') || localStorage.getItem('refresh_token');

      if (userType !== 'admin' && isExpiredUserToken && refreshToken && !config._retry) {
        config._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = axios
              .post(
                `${API_URL}/api/auth/refresh`,
                { refresh_token: refreshToken },
                { headers: { 'Content-Type': 'application/json' }, withCredentials: true, timeout: 8000 }
              )
              .then((response) => {
                if (!response.data?.success || !response.data?.session) {
                  throw new Error('Session refresh failed');
                }
                storeUserSession(response.data.session);
                return response.data.session;
              })
              .finally(() => {
                refreshPromise = null;
              });
          }

          const session = await refreshPromise;
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${session.access_token}`;
          return api(config);
        } catch (_) {
          clearStoredAuth();
          redirectRegularUserToLogin();
        }
      } else if (
        message.includes('Session invalid from this device') ||
        message.includes('device') ||
        isExpiredUserToken
      ) {
        clearStoredAuth();
        if (userType === 'admin') {
          window.location.href = '/admin/login';
        } else {
          redirectRegularUserToLogin();
        }
      }
    }
    return Promise.reject(error);
  }
);

function storeUserSession(session) {
  if (!session?.access_token) return;
  localStorage.setItem('access_token', session.access_token);
  localStorage.setItem('user_access_token', session.access_token);
  if (session.refresh_token) {
    localStorage.setItem('refresh_token', session.refresh_token);
    localStorage.setItem('user_refresh_token', session.refresh_token);
  }
  localStorage.setItem('user_type', 'user');
}

export const authService = {
  // Regular user login (local MySQL auth)
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.success) {
      storeUserSession(response.data.session);
    }
    return response.data;
  },

  // Generate device fingerprint for token binding
  generateDeviceFingerprint: () => {
    // eslint-disable-next-line no-restricted-globals
    const screenInfo = typeof window !== 'undefined' && window.screen 
      ? `${window.screen.width}x${window.screen.height}` 
      : 'unknown';
    // eslint-disable-next-line no-restricted-globals
    const colorDepth = typeof window !== 'undefined' && window.screen 
      ? window.screen.colorDepth 
      : 'unknown';
    
    const components = [
      navigator.userAgent,
      navigator.language,
      screenInfo,
      colorDepth,
      new Date().getTimezoneOffset(),
      navigator.platform,
      navigator.hardwareConcurrency || 'unknown',
    ];
    
    // Create a hash-like string
    const fingerprint = components.join('::');
    return btoa(fingerprint).substring(0, 64);
  },

  // Admin login (via local JWT)
  adminLogin: async (email, password, rememberMe = false) => {
    const deviceFingerprint = authService.generateDeviceFingerprint();
    const response = await api.post('/admin/login', { 
      email, 
      password, 
      rememberMe,
      deviceFingerprint 
    });
    if (response.data.success) {
      localStorage.setItem('access_token', response.data.token);
      localStorage.setItem('admin_access_token', response.data.token);
      localStorage.setItem('user_type', 'admin');
      localStorage.setItem('device_fingerprint', deviceFingerprint);
      if (rememberMe) {
        localStorage.setItem('remember_me', 'true');
        // Store expiration time (6 days from now)
        const expiresAt = new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString();
        localStorage.setItem('token_expires', expiresAt);
      } else {
        localStorage.removeItem('remember_me');
        localStorage.removeItem('token_expires');
      }
    }
    return response.data;
  },

  // Admin logout
  adminLogout: async () => {
    const response = await api.post('/admin/logout');
    const adminToken = localStorage.getItem('admin_access_token');
    if (localStorage.getItem('access_token') === adminToken) {
      localStorage.removeItem('access_token');
    }
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_type');
    localStorage.removeItem('device_fingerprint');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expires');
    return response.data;
  },

  // Verify admin token
  verifyAdmin: async () => {
    const response = await api.get('/admin/verify');
    return response.data;
  },

  signup: async (email, password, fullName) => {
    const response = await api.post('/auth/signup', { email, password, fullName });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    if (response.data.success && response.data.session) {
      storeUserSession(response.data.session);
    }
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  getGoogleAuthUrl: async () => {
    const response = await api.get('/auth/google/url');
    return response.data;
  },

  exchangeOAuthCode: async (code) => {
    const response = await api.post('/auth/exchange', { code });
    if (response.data.success) {
      storeUserSession(response.data.session);
    }
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: async () => {
    // Check if admin or regular user
    const userType = localStorage.getItem('user_type');
    if (userType === 'admin') {
      return authService.adminLogout();
    }
    const response = await api.post('/auth/logout');
    const userToken = localStorage.getItem('user_access_token');
    if (localStorage.getItem('access_token') === userToken) {
      localStorage.removeItem('access_token');
    }
    localStorage.removeItem('user_access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_refresh_token');
    localStorage.removeItem('user_type');
    localStorage.removeItem('device_fingerprint');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('token_expires');
    return response.data;
  },

  refreshToken: async () => {
    const refresh_token = localStorage.getItem('user_refresh_token') || localStorage.getItem('refresh_token');
    if (!refresh_token) throw new Error('No refresh token');
    
    const response = await api.post('/auth/refresh', { refresh_token });
    if (response.data.success) {
      storeUserSession(response.data.session);
    }
    return response.data;
  },

  isAuthenticated: () => {
    const userType = localStorage.getItem('user_type');
    const token = userType === 'admin'
      ? (localStorage.getItem('admin_access_token') || localStorage.getItem('access_token'))
      : (localStorage.getItem('user_access_token') || localStorage.getItem('access_token'));
    if (!token) return false;
    
    // Check if token is expired (for remember me)
    const expiresAt = localStorage.getItem('token_expires');
    if (expiresAt) {
      if (new Date() > new Date(expiresAt)) {
        // Token expired, clear storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_access_token');
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_refresh_token');
        localStorage.removeItem('user_type');
        localStorage.removeItem('remember_me');
        localStorage.removeItem('token_expires');
        return false;
      }
    }
    
    return true;
  },

  isShowSignup: () => {
    const today = new Date().toISOString().split("T")[0]; // e.g. "2026-07-15"
    const lastShownDate = localStorage.getItem('last_signup_shown_date');

    if (lastShownDate !== today) {
      localStorage.setItem('last_signup_shown_date', today);
      return true;
    }
    return false;
  },

  isAdmin: () => {
    return localStorage.getItem('user_type') === 'admin';
  },

  // Validate that a real user session is still active (not just token present in storage)
  ensureUserSession: async () => {
    if (!authService.isAuthenticated()) return false;
    try {
      const res = await authService.getCurrentUser();
      return !!res?.success;
    } catch (err) {
      localStorage.removeItem('user_access_token');
      localStorage.removeItem('user_refresh_token');
      if (localStorage.getItem('user_type') !== 'admin') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      localStorage.removeItem('user_type');
      return false;
    }
  },

  // Reset password using recovery key (public endpoint)
  resetPasswordWithRecoveryKey: async (email, recoveryKey, newPassword) => {
    const response = await api.post('/admin/reset-password', { 
      email, 
      recoveryKey, 
      newPassword 
    });
    return response.data;
  },

  // Change admin password (requires current password and authentication)
  changeAdminPassword: async (currentPassword, newPassword) => {
    const response = await api.post('/admin/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  triggerResetPassword: async (email) => {
    const response = await api.put('/auth/reset-password', {
      email,
    });
    return response.data;
  },

  resetPassword: async (password, token) => {
    const response = await api.post('/auth/reset-password', {
      password,
      token,
    });
    return response.data;
  }
};

export default authService;
